import { GoAiguaButton } from "components/generic";
import { useEffect, useState } from "react";

import { useIntl } from "react-intl";
import { DropDownBox, SelectBox, TagBox, TreeView } from "devextreme-react";
import CustomStore from "devextreme/data/custom_store";
import { useStore } from "react-redux";

import {
    addUserToGroup,
    getRolesByGroupId,
    getUserGroups,
    removeUserToGroup,
} from "services/requests/v1/user";

import {
    GoAiguaSelectOption,
    SetRequestError,
    SubscribeContentType,
    UnsubscribeContentType,
} from "types/generic";

import { refreshPage } from "utils/functions";

import { AxiosPromise } from "axios";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { useKeycloak } from "@react-keycloak/web";
import { isTokenKCApiExpired } from "utils/functions";

type UserAssigmentGroupType = {
    availableGroups: GoAiguaSelectOption[];
    provenance: string;
    userUid?: string;
    setOpenModal: (openModal: boolean) => void;
    token: string;
    subscribeContent: SubscribeContentType;
    unsubscribeContent: UnsubscribeContentType;
    setError: SetRequestError;
};

const UserAssigmentGroup = ({
    availableGroups,
    provenance,
    userUid,
    setOpenModal,
    token,
    setError,
    subscribeContent,
    unsubscribeContent,
}: UserAssigmentGroupType) => {
    const intl = useIntl();

    const [availableRoles, setAvailableRoles] = useState<string[]>([]);

    const [treeViewState, setTreeView] = useState<any>();
    const [selectedGroup, setSelectedGroup] = useState<any[]>([]);

    const store = useStore();
    const currentUserUid = store.getState().keycloak.sub;

    const {
        keycloak,
    }: {
        initialized: boolean;
        keycloak: Keycloak.KeycloakInstance;
    } = useKeycloak();

    const checkIfTokenIsValid = () => {
        if (isTokenKCApiExpired(token)) {
            keycloak.logout();
        }
    };

    useEffect(() => {
        checkIfTokenIsValid();
        // Depends on every action that will need KC API Call
    }, [token, availableGroups, availableRoles, selectedGroup]);

    useEffect(() => {
        checkIfTokenIsValid();
        if (availableGroups && availableGroups.length > 0 && userUid) {
            getUserGroups(provenance, userUid).then((res) => {
                console.log(res);
                const userGroups: any[] = res.data;
                setSelectedGroup(userGroups.map((group) => group.id));
            });
        }
    }, [availableGroups]);

    useEffect(() => {
        checkIfTokenIsValid();
        if (selectedGroup && selectedGroup.length > 0) {
            let rolesAux: any = [];
            selectedGroup.forEach((value) => {
                getRolesByGroupId(value).then((res) => {
                    rolesAux = rolesAux.concat(res.data.roles);
                    let roles: Set<string> = new Set(rolesAux);
                    setAvailableRoles([...roles]);
                });
            });
        } else {
            setAvailableRoles([]);
        }
    }, [selectedGroup]);

    const syncTreeViewSelection = (e: any) => {
        const treeView =
            (e.component.selectItem && e.component) ||
            (treeViewState && treeViewState.instance);

        if (treeView) {
            if (e.value === null) {
                treeView.unselectAll();
            } else {
                const values = e.value || selectedGroup;
                values &&
                    values.forEach((value: any) => {
                        treeView.selectItem(value);
                    });
            }
        }

        if (e.value !== undefined) {
            setSelectedGroup(e.value);
        }
    };

    const updateUserGroups = async (userUid: string) => {
        const contentName = "assinguserToGroup";
        subscribeContent(contentName);

        const promises: AxiosPromise[] = [];
        availableGroups.forEach((availableGroup) => {
            if (selectedGroup && selectedGroup.includes(availableGroup.value)) {
                promises.push(addUserToGroup(+availableGroup.value, userUid));
            } else {
                promises.push(
                    removeUserToGroup(+availableGroup.value, userUid)
                );
            }
        });

        return Promise.all(promises)
            .then(() => unsubscribeContent(contentName, true))
            .catch(() => setError(contentName));
    };

    return (
        <>
            <div className={"c-user-assigment__group-panel"}>
                <div className={"c-user-assigment__group-panel__title"}>
                    {intl.formatMessage({
                        id: "app.Component.Roles.GroupAssignments",
                    })}
                    <hr />
                </div>
                <div
                    className={
                        "c-user-assigment__group-panel__available-selector"
                    }
                >
                    <div
                        className={
                            "c-user-assigment__group-panel__available-selector__title"
                        }
                    >
                        {intl.formatMessage({
                            id: "app.Component.Roles.AvailableGroups",
                        })}
                    </div>
                    {availableGroups && (
                        <DropDownBox
                            dataSource={
                                new CustomStore({
                                    loadMode: "raw",
                                    key: "value",
                                    load() {
                                        return availableGroups;
                                    },
                                })
                            }
                            value={selectedGroup}
                            displayExpr="label"
                            valueExpr="value"
                            placeholder=""
                            showClearButton={true}
                            onValueChanged={syncTreeViewSelection}
                            contentRender={() => (
                                <TreeView
                                    ref={(ref) => setTreeView(ref)}
                                    dataSource={
                                        new CustomStore({
                                            loadMode: "raw",
                                            key: "value",
                                            load() {
                                                return availableGroups;
                                            },
                                        })
                                    }
                                    dataStructure="plain"
                                    keyExpr="value"
                                    selectionMode="multiple"
                                    showCheckBoxesMode="normal"
                                    selectNodesRecursive={false}
                                    displayExpr="label"
                                    selectByClick={true}
                                    onContentReady={syncTreeViewSelection}
                                    onItemSelectionChanged={(e) =>
                                        setSelectedGroup(
                                            e.component.getSelectedNodeKeys()
                                        )
                                    }
                                    noDataText={intl.formatMessage({
                                        id: "app.users.no_available_groups",
                                    })}
                                />
                            )}
                        />
                    )}
                </div>
                <div
                    className={"c-user-assigment__group-panel__roles-assigned"}
                >
                    <div
                        className={
                            "c-user-assigment__group-panel__roles-assigned__title"
                        }
                    >
                        {intl.formatMessage({
                            id: "app.Component.Roles.RolesAssignedToGroup",
                        })}
                    </div>
                    <div
                        className={
                            "c-user-assigment__group-panel__roles-assigned__list"
                        }
                    >
                        {availableRoles.map((role, index) => {
                            return (
                                <div
                                    key={index}
                                    className={`c-user-assigment__group-panel__roles-assigned__list-element`}
                                >
                                    {role}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className={"c-user-assigment__footer"}>
                <GoAiguaButton
                    text={intl.formatMessage({ id: "app.users.cancel" })}
                    className={"c-user-assigment__footer__cancel-button"}
                    onClick={() => {
                        setOpenModal(false);
                    }}
                />
                <GoAiguaButton
                    text={intl.formatMessage({ id: "app.users.save_changes" })}
                    className={"c-user-assigment__footer__save-button"}
                    onClick={() => {
                        checkIfTokenIsValid();
                        if (availableGroups && userUid) {
                            updateUserGroups(userUid).then((res) => {
                                if (userUid === currentUserUid) {
                                    refreshPage();
                                }
                                setOpenModal(false);
                            });
                        }
                    }}
                />
            </div>
        </>
    );
};

const mapStateToProps = (state: any) => {
    return {
        token: state.keycloak.token,
    };
};
const mapDispatchToProps = (dispatch: Dispatch) => {
    return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(UserAssigmentGroup);
